import { Box, Chip, ListItemButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FlashcardDeckStats from '@/components/flashcard/flashcard-deck-stats';
import CircularProgressLabel from '@/components/shared/circular-progress-label';
import type { FlashcardDeckItem as FlashcardDeckItemType } from '@/types';

export default function FlashcardDeckItem({
  item: {
    indent,
    deck: {
      access,
      hasAccess,
      slug,
      title,
      stats,
      stats: { progress, total, started },
    },
    style,
    prefix,
  },
  hasMembership,
  onNavigate,
}: {
  item: FlashcardDeckItemType;
  hasMembership: boolean;
  onNavigate: (slug: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <ListItemButton
      key={slug}
      onClick={() => onNavigate(slug)}
      className={`${style}`}
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        pr: 1.5,
        pl: { xs: 1.5, md: indent * 2 + 1.5 },
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderBottom: 0,
        '&:last-child': {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
        '&.item, &.section': {
          fontSize: (theme) => theme.typography.body2.fontSize,
        },
        lineHeight: 1.2,
        '&.h1': { fontSize: (theme) => theme.typography.h5.fontSize },
        '&.h2': { fontSize: (theme) => theme.typography.h6.fontSize },
        '&.h3': { fontSize: (theme) => theme.typography.h6.fontSize },
        color: (theme) => (hasAccess ? theme.palette.text.primary : theme.palette.text.disabled),
      }}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          fontSize: 'inherit',
        }}>
        <Typography sx={{ fontSize: 'inherit' }}>
          {prefix && (
            <Typography color="gray" component="span" mr={1}>
              {prefix}
            </Typography>
          )}
          {title} ({total} {t('flashcard.common.card', { count: total })})
        </Typography>
        {!hasMembership && access !== 'membership' && (
          <Chip
            label={t('flashcard.deckItem.freeChip')}
            size="small"
            variant="outlined"
            color="success"
            sx={{ mr: { md: 1 } }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: { xs: 2, md: 0 },
        }}>
        {hasAccess && started && (
          <Box sx={{ display: { xs: 'none', md: 'block' }, mr: 2 }}>
            <CircularProgressLabel value={progress} />
          </Box>
        )}
        {hasAccess && started && <FlashcardDeckStats stats={stats} />}
        {hasAccess && !started && (
          <Chip
            size="small"
            variant="outlined"
            color="default"
            label={t('flashcard.deckItem.startNow')}
          />
        )}
      </Box>
    </ListItemButton>
  );
}
